import React, { useContext, useMemo, useState } from 'react';


// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import HostTarget from '../../../types/host/HostTarget';
import ChartPlotLine from '../../../types/ChartPlotLine';

// Constants.
import { getConvertedTimeToUTC } from "../../../helpers/utils";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import TimeSeriesChart from "../../chart/echarts/TimeSeriesChart";
import { EChartPoint, EChartTimeSeries } from "../../chart/echarts/types";
import {
    CHART_COLOURS_STATEMENTS, CHART_DATA_TYPE,
    COLOUR_HOST_MEMORY,
    COLOUR_HOST_PROCESSOR,
    DATA_LOADED,
    DATA_LOADING
} from "../../Constants";
import Helper from "../../../helpers/Helper";
import ConditionalRender from "../../../helpers/ConditionalRender";
import HostApi from "../../../api/host/HostApi";
import InstanceApi from "../../../api/instance/InstanceApi";

function HostsOverTimeCharts(props: { instance: InstanceTarget, hosts: HostTarget[], plotLines: ChartPlotLine[] }) {
    const [processorSeries, setProcessorSeries] = useState<EChartTimeSeries[]>([]);
    const [memorySeries, setMemorySeries] = useState<EChartTimeSeries[]>([]);
    const [diskSeries, setDiskSeries] = useState<EChartTimeSeries[]>([]);
    const [diskReadPeaks, setDiskReadPeaks] = useState<number[]>([])
    const [diskWritePeaks, setDiskWritePeaks] = useState<number[]>([])
    const [loading, setLoading] = useState(DATA_LOADING)
    const timeRangeContext = useContext(TimeRangeContext)

    const diskPeaks = useMemo(() => {
        return {
            read: Helper.getPeakValue(diskReadPeaks),
            write: Helper.getPeakValue(diskWritePeaks)
        }
    }, [diskReadPeaks, diskWritePeaks])

    const memoryPeak = useMemo(() => {
        let peaks: number[] = []
        memorySeries.forEach((serie: any) => {
            peaks = peaks.concat(serie.data.map((i: any) => i[1]))
        })
        return Helper.getPeakValue(peaks)
    }, [memorySeries])

    const processorPeak = useMemo(() => {
        let peaks: number[] = []
        processorSeries.forEach((serie: any) => {
            peaks = peaks.concat(serie.data.map((i: any) => i[1]))
        })
        return Helper.getPeakValue(peaks)
    }, [processorSeries])

    // Build the per-host CPU / memory / disk over-time series. Each host now needs a single v2
    // time call instead of one call per metric, so the request count drops from 4N+1 to N+1.
    useMemo(() => {
        async function getHostsData() {
            setLoading(DATA_LOADING);
            const hosts: HostTarget[] = []
            const hostMappings = await InstanceApi.getInstanceHostByInstance(props.instance.id);
            for (let index = 0; index < hostMappings.length; index++) {
                const matchedHosts = props.hosts.filter(host => host.id === hostMappings[index].hostid);
                if (matchedHosts.length === 1) {
                    hosts.push(matchedHosts[0]);
                }
            }

            const query = buildApiQuery(timeRangeContext.timeRange);
            const responses = await Promise.all(
                hosts.map(host => HostApi.getHostStatisticV2Time(query, [host.id]).then(data => ({ host, data })))
            );

            const memSeries: any[] = []
            const procSeries: any[] = []
            const diskSeries: any[] = []
            const readPeaks: number[] = []
            const writePeaks: number[] = []

            responses.forEach(({ host, data }) => {
                procSeries.push({
                    name: `CPU - ${host.name}`,
                    color: COLOUR_HOST_PROCESSOR,
                    chartType: 'line',
                    data: data.map((item): EChartPoint => [getConvertedTimeToUTC(item), Math.round(Number(item.cpuutilisation) || 0)]),
                });

                memSeries.push({
                    name: `Memory - ${host.name}`,
                    color: COLOUR_HOST_MEMORY,
                    chartType: 'line',
                    data: data.map((item): EChartPoint => [getConvertedTimeToUTC(item), Math.round(Number(item.memoryutilisation) || 0)]),
                });

                const diskMetrics: string[] = ['time spent reading (ms)', 'time spent writing (ms)'];
                diskMetrics.forEach((metric, idx) => {
                    const points: number[][] = data.map(item => [
                        getConvertedTimeToUTC(item),
                        Math.round(Number(item[metric]) || 0),
                    ]);
                    diskSeries.push({
                        name: `${metric} - ${host.name}`,
                        color: CHART_COLOURS_STATEMENTS[idx],
                        chartType: 'bar',
                        data: points as EChartPoint[],
                    });

                    const values = points.map(p => p[1]);
                    if (metric === 'time spent reading (ms)') {
                        readPeaks.push(Helper.getPeakValue(values));
                    } else {
                        writePeaks.push(Helper.getPeakValue(values));
                    }
                });
            });

            setMemorySeries(memSeries)
            setProcessorSeries(procSeries)
            setDiskSeries(diskSeries)
            setDiskWritePeaks(writePeaks)
            setDiskReadPeaks(readPeaks)
            setLoading(DATA_LOADED);
        }

        void getHostsData();
    }, [props.hosts, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.instance.id]);

    // Build the processor and memory chart.
    const processorMemoryChart = useMemo(() => {
        if (processorSeries.length && memorySeries.length) {
            return (
                <React.Fragment>
                    <span className="peak">Peak CPU {processorPeak}%, Peak Memory {memoryPeak}%</span>
                    <br/><br/>
                    <TimeSeriesChart series={[...processorSeries, ...memorySeries]}
                                     tooltipType={CHART_DATA_TYPE.GENERIC}
                                     plotLines={props.plotLines} legend={true}/>
                </React.Fragment>
            );
        }

        return null
    }, [processorSeries, memorySeries, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.plotLines, memoryPeak, processorPeak]);

    // Build the disk read and writes chart.
    const diskChart = useMemo(() => {
        if (diskSeries.length && diskPeaks.read && diskPeaks.write) {
            return (
                <React.Fragment>
                    <span className="peak">Peak Time Spent Reading {Helper.getTimeInEnglish(diskPeaks.read)}, Spent Writing {Helper.getTimeInEnglish(diskPeaks.write)}</span>
                    <br/><br/>
                    <TimeSeriesChart series={diskSeries} tooltipType={CHART_DATA_TYPE.TIME}
                                     plotLines={props.plotLines} legend={true}/>
                </React.Fragment>
            );
        }

        return null
    }, [diskSeries, diskPeaks, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.plotLines])

    return (
        <React.Fragment>
            <ConditionalRender if={loading === DATA_LOADING}>
                <div className="w-100 text-center text-muted mt-3 time-chart">
                    <div className="loader spinner">
                    </div>
                    <p className="mt-3">
                        Loading data...
                    </p>
                </div>
            </ConditionalRender>
            <ConditionalRender if={loading === DATA_LOADED}>
                {processorMemoryChart}
                {diskChart}
            </ConditionalRender>
        </React.Fragment>
    );
}

export default HostsOverTimeCharts;
